import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ImageModule } from 'primeng/image';

import {
  ReceiptSettingsService,
  ReceiptSettings,
  ReceiptSettingsDto,
} from '../../services/receipt-settings';
import { ClientService, Client } from '../../services/client';
import { FileService } from '../../services/file';
import { DocUploadComponent } from '../../shared/doc-upload';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

type ScopeMode = 'MAIN' | 'CLIENT';

/**
 * Receipt Settings — single-table admin CRUD for the issuer block printed on every payment receipt.
 * Main / Partner (clientId == null) is the default; client-specific rows override it for that client's
 * members. Modelled on the payment-types master screen (p-table + one add/edit p-dialog); deletes
 * confirm via SweetAlert2 (ToastService), matching the other masters.
 */
@Component({
  selector: 'app-receipt-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ImageModule,
    DocUploadComponent,
  ],
  templateUrl: './receipt-settings.html',
  styleUrls: ['./receipt-settings.css'],
})
export class ReceiptSettingsPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(ReceiptSettingsService);
  private clientSvc = inject(ClientService);
  private fileSvc = inject(FileService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<ReceiptSettings[]>([]);

  /** Dialog scope selector (drives the client picker visibility); clientId lives in the form. */
  scopeMode = signal<ScopeMode>('MAIN');
  readonly scopeOptions = [
    { value: 'MAIN' as ScopeMode, label: 'Main / Partner (default)' },
    { value: 'CLIENT' as ScopeMode, label: 'Specific client' },
  ];

  /** Client options for the dialog picker + header filter — active clients only. */
  clientOptions = signal<{ value: number; label: string }[]>([]);
  clientsLoaded = signal(false);

  /** Header "Filter client" — null shows all rows; a client id shows byClient() (its rows + Main). */
  filterClientId = signal<number | null>(null);

  /** Logo preview URL bound to the dialog p-image (kept reactive under zoneless). */
  logoPreview = signal('');
  /** Resolved presigned URLs keyed by the stored logo value (S3 guid → url); http URLs render as-is. */
  logoUrls = signal<Record<string, string>>({});

  /** Main / Partner first, then by client name. (Backend GetAll already returns this order.) */
  visible = computed(() =>
    [...this.rows()].sort((a, b) => {
      const am = a.clientId == null ? 0 : 1;
      const bm = b.clientId == null ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.clientName ?? '').localeCompare(b.clientName ?? '');
    }),
  );

  form: FormGroup = this.fb.group({
    id: [0],
    clientId: [null as number | null],
    issuerName: ['', Validators.required],
    issuerAddress: [''],
    issuerEmail: [''],
    issuerPhone: [''],
    issuerGstin: [''],
    logoUrl: [''],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    // Keep the dialog logo preview in sync when the uploader stores a new guid (or on reset/edit).
    this.form.get('logoUrl')!.valueChanges.subscribe((v) => this.setLogoPreview(v));
    this.load();
    this.loadClients();
  }

  // ===================== data =====================
  load() {
    this.loading.set(true);
    const cid = this.filterClientId();
    const req = cid != null ? this.svc.byClient(cid) : this.svc.all();
    req.subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load receipt settings.');
          this.rows.set([]);
          return;
        }
        const list = res?.data ?? [];
        this.rows.set(list);
        this.resolveLogos(list);
      },
      error: () => this.loading.set(false),
    });
  }

  /** A logo stored as an S3 guid → presigned URL for the row thumbnail (http URLs render as-is). */
  private resolveLogos(list: ReceiptSettings[]) {
    for (const r of list) {
      const v = (r.logoUrl ?? '').trim();
      if (!v || /^https?:\/\//i.test(v) || this.logoUrls()[v]) continue;
      this.fileSvc.url(v).subscribe({
        next: (u) => {
          if (u) this.logoUrls.update((m) => ({ ...m, [v]: u }));
        },
      });
    }
  }

  /** Render URL for a row's logo: a direct http URL, the resolved presigned URL, or '' (fallback). */
  logoSrc(r: ReceiptSettings): string {
    const v = (r.logoUrl ?? '').trim();
    if (!v) return '';
    if (/^https?:\/\//i.test(v)) return v;
    return this.logoUrls()[v] ?? '';
  }

  /** Resolve the dialog preview for the current logo value (guid → presigned URL, cached). */
  private setLogoPreview(v: string | null | undefined) {
    const s = (v ?? '').toString().trim();
    if (!s) {
      this.logoPreview.set('');
      return;
    }
    if (/^https?:\/\//i.test(s)) {
      this.logoPreview.set(s);
      return;
    }
    const cached = this.logoUrls()[s];
    if (cached) {
      this.logoPreview.set(cached);
      return;
    }
    this.fileSvc.url(s).subscribe({
      next: (u) => {
        if (!u) return;
        this.logoUrls.update((m) => ({ ...m, [s]: u }));
        // Only apply if the form still holds this value (guard against a fast re-pick).
        if ((this.form.get('logoUrl')?.value ?? '').toString().trim() === s) this.logoPreview.set(u);
      },
    });
  }

  private loadClients() {
    this.clientSvc.cltGetAll().subscribe({
      next: (res) => {
        const list: Client[] = (res?.data ?? []) as Client[];
        this.clientOptions.set(
          list
            .filter((c) => c.isActive !== false)
            .map((c) => ({
              value: c.id,
              label: c.clientCode ? `${c.clientName} (${c.clientCode})` : c.clientName,
            })),
        );
        this.clientsLoaded.set(true);
      },
    });
  }

  clientLabel(id: number): string {
    return this.clientOptions().find((o) => o.value === id)?.label ?? `Client #${id}`;
  }

  onFilterChange(clientId: number | null) {
    this.filterClientId.set(clientId ?? null);
    this.load();
  }

  clearFilter() {
    if (this.filterClientId() == null) return;
    this.filterClientId.set(null);
    this.load();
  }

  // ===================== add / edit =====================
  onScopeChange(mode: ScopeMode) {
    this.scopeMode.set(mode);
    if (mode === 'MAIN') this.form.get('clientId')!.setValue(null);
  }

  openAdd() {
    this.editing.set(false);
    this.scopeMode.set('MAIN');
    this.form.reset({
      id: 0,
      clientId: null,
      issuerName: '',
      issuerAddress: '',
      issuerEmail: '',
      issuerPhone: '',
      issuerGstin: '',
      logoUrl: '',
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(r: ReceiptSettings) {
    this.editing.set(true);
    this.scopeMode.set(r.clientId == null ? 'MAIN' : 'CLIENT');
    this.form.reset({
      id: r.id,
      clientId: r.clientId ?? null,
      issuerName: r.issuerName ?? '',
      issuerAddress: r.issuerAddress ?? '',
      issuerEmail: r.issuerEmail ?? '',
      issuerPhone: r.issuerPhone ?? '',
      issuerGstin: r.issuerGstin ?? '',
      logoUrl: r.logoUrl ?? '',
      isActive: r.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    const v = this.form.getRawValue();
    if (this.form.get('issuerName')?.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Issuer name is required.');
      return;
    }
    if (this.scopeMode() === 'CLIENT' && !v.clientId) {
      this.toast.warning('Select a client for the client-specific issuer, or switch the scope to Main / Partner.');
      return;
    }

    const clientId = this.scopeMode() === 'CLIENT' ? Number(v.clientId) : null;
    const dto: ReceiptSettingsDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientId,
      issuerName: (v.issuerName || '').trim(),
      issuerAddress: (v.issuerAddress || '').trim() || null,
      issuerEmail: (v.issuerEmail || '').trim() || null,
      issuerPhone: (v.issuerPhone || '').trim() || null,
      issuerGstin: (v.issuerGstin || '').trim() || null,
      logoUrl: (v.logoUrl || '').trim() || null,
      isActive: !!v.isActive,
    };

    const req = this.editing() ? this.svc.update(dto) : this.svc.insert(dto);
    this.saving.set(true);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          // 400 (IssuerName required / Id required) or 404 (client / row not found) — surface verbatim.
          this.toast.error(res?.message || 'Failed to save receipt setting.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Receipt setting updated' : 'Receipt setting created'));
        // Advisory: activating a row while another active row exists for the same scope has no effect
        // until the older one is disabled (latest active wins).
        if (dto.isActive) this.warnDuplicateActiveScope(dto.clientId ?? null, dto.id);
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== enable / disable / delete =====================
  toggleActive(r: ReceiptSettings) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    const req = next ? this.svc.enable(r.id) : this.svc.disable(r.id);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
          return;
        }
        if (next) this.warnDuplicateActiveScope(r.clientId ?? null, r.id);
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<ReceiptSettings>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  /** Advisory hint only — the backend does NOT auto-deactivate siblings (highest active Id wins). */
  private warnDuplicateActiveScope(clientId: number | null, selfId: number) {
    const other = this.rows().some(
      (x) => x.id !== selfId && x.isActive && (x.clientId ?? null) === clientId,
    );
    if (!other) return;
    const scope = clientId == null ? 'Main / Partner' : this.clientLabel(clientId);
    this.toast.info(
      `Another active row already exists for ${scope}. Only the latest active row is used — disable the old one to make this one take effect.`,
      'Heads up',
    );
  }

  async remove(r: ReceiptSettings) {
    const isMain = r.clientId == null;
    const scope = isMain ? 'the Main / Partner issuer' : `the issuer for ${r.clientName ?? this.clientLabel(r.clientId!)}`;
    const warn = isMain
      ? 'This permanently deletes the Main / Partner row. Receipts will fall back to the appsettings Receipt:* defaults until you add another Main / Partner row.'
      : `This permanently deletes ${scope}. That client's receipts will fall back to the Main / Partner issuer.`;
    const ok = await this.toast.confirm(warn, 'Delete receipt setting?', 'Delete');
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete receipt setting.');
          return;
        }
        this.toast.success(res?.message || 'Receipt setting deleted');
        this.load();
      },
    });
  }
}
